<script>
import NotificationAlertsComponent from '../features/NotificationAlertsComponent';

export default {
  name: 'NotificationAlertsView',
  components: {},
  mixins: [NotificationAlertsComponent],
  props: {
    /**
     * `header`  → campana sobre fondo claro (admin / profesor).
     * `sidebar` → campana integrada en una fila del sidebar del estudiante.
     */
    variant: {
      type: String,
      default: 'header',
      validator: (value) => ['header', 'sidebar'].includes(value),
    },
    /**
     * `true` cuando el sidebar está colapsado: el texto de la fila se oculta,
     * así que el contador vuelve a apoyarse sobre la campana para no perder
     * la información.
     */
    collapsed: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    /**
     * El total de nuevas notificaciones se propaga al padre para que pueda
     * pintarlo DESPUÉS del texto de la opción (fila "Notificaciones" del
     * sidebar del estudiante). Se mantiene una única fuente de verdad: el
     * contador vive en este componente y el padre solo lo refleja.
     */
    totalAlertsNotifications(value) {
      this.$emit('total-change', value);
    },
  },
  computed: {
    isSidebarVariant() {
      return this.variant === 'sidebar';
    },
    // En el sidebar (borde izquierdo de la pantalla) el popper debe abrirse
    // hacia la derecha; en el header se mantiene la apertura hacia abajo.
    dropdownPlacement() {
      return this.isSidebarVariant ? 'right-start' : 'bottom-end';
    },
    containerClass() {
      return `notification-alerts-container--${this.variant}`;
    },
    bellIconClass() {
      return this.isSidebarVariant ? 'fas fa-bell menu--icon' : 'fas fa-bell bell-icon';
    },
    /**
     * El contador se pinta DESPUÉS del texto de la fila cuando el sidebar
     * está expandido. Colapsado (el texto se oculta) se muestra sobre la
     * campana para no perder la información.
     */
    showBellBadge() {
      return !this.isSidebarVariant || this.collapsed;
    },
    allNotificationsRoute() {
      if (this.$route && this.$route.path) {
        if (this.$route.path.startsWith('/profesor')) return '/profesor/notificaciones';
        if (this.$route.path.startsWith('/estudiante')) return '/estudiante/notificaciones';
      }
      return '/admin/notificaciones';
    },
  },
  methods: {
    /**
     * En variante `sidebar` el trigger vive dentro de un <router-link>.
     * `preventDefault` cancela la navegación nativa del <a> y `stopPropagation`
     * impide que el evento alcance el handler del router-link, de modo que el
     * clic abre el dropdown sin navegar a la página de notificaciones.
     */
    onTriggerClick(event) {
      if (!this.isSidebarVariant || !event) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
    },
  },
};
</script>

<template>
  <div
    class="notification-alerts-container"
    :class="containerClass"
  >
    <el-dropdown
      trigger="click"
      popper-class="notification-dropdown-popper"
      :placement="dropdownPlacement"
    >
      <div
        class="notification-trigger"
        title="Notificaciones"
        aria-label="Notificaciones"
        role="button"
        tabindex="0"
        @click="onTriggerClick"
      >
        <el-badge
          v-if="showBellBadge"
          :value="totalAlertsNotifications"
          :hidden="!totalAlertsNotifications || totalAlertsNotifications === 0"
          :max="99"
          class="notification-badge"
        >
          <i :class="bellIconClass" />
        </el-badge>

        <!-- En el sidebar expandido el contador va DESPUÉS del texto de la fila
             ("Notificaciones"), por eso aquí la campana va sin badge. -->
        <i
          v-else
          :class="bellIconClass"
        />
      </div>

      <template #dropdown>
        <el-dropdown-menu class="notification-dropdown-menu">
          <!-- Cabecera de notificaciones -->
          <div class="notification-header">
            <div class="header-title-box">
              <div class="header-icon-wrap">
                <i class="fas fa-bell header-icon" />
              </div>
              <span class="header-title">Notificaciones</span>
            </div>
            <span
              v-if="totalAlertsNotifications && totalAlertsNotifications > 0"
              class="header-badge-pill"
            >
              {{ totalAlertsNotifications }} nueva(s)
            </span>
          </div>

          <!-- Listado de notificaciones -->
          <div
            v-if="notificationAlerts && notificationAlerts.length"
            class="notification-list"
          >
            <el-dropdown-item
              v-for="notification in notificationAlerts"
              :key="notification.id"
              class="notification-item"
              :class="{ 'is-unread': !notification.isRead }"
              @click="onSelectNotification(notification)"
            >
              <div class="notification-avatar-box">
                <img
                  v-if="notification.sender && notification.sender.profileImageUrl"
                  :src="notification.sender.profileImageUrl"
                  alt="avatar"
                >
                <i
                  v-else
                  class="fas fa-bell"
                />
              </div>
              <div class="notification-content">
                <div
                  v-if="notification.header"
                  class="notif-title"
                >
                  {{ notification.header }}
                </div>
                <div
                  v-if="notification.message"
                  class="notif-body"
                  v-html="notification.message"
                />
              </div>
              <span
                v-if="!notification.isRead"
                class="notif-unread-dot"
                title="Nueva alerta"
              />
            </el-dropdown-item>
          </div>

          <!-- Estado vacío -->
          <div
            v-else
            class="notification-empty"
          >
            <div class="empty-icon-circle">
              <i class="far fa-bell" />
            </div>
            <strong class="empty-title">Estás al día</strong>
            <span class="empty-text">No tienes notificaciones pendientes</span>
          </div>

          <!-- Footer con enlace general -->
          <div class="notification-footer">
            <router-link
              :to="allNotificationsRoute"
              class="view-all-link"
            >
              <span>Ver todas las notificaciones</span>
              <i class="fas fa-arrow-right view-all-icon" />
            </router-link>
          </div>
        </el-dropdown-menu>
      </template>
    </el-dropdown>
  </div>
</template>

<style scoped>
.notification-alerts-container {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.notification-trigger {
  cursor: pointer;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 0;
  margin: 0;
  outline: none;
  color: #94a3b8;
  transition: all 0.15s ease;
  user-select: none;
}

.notification-trigger:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.25);
  color: #ffffff;
}

.notification-trigger:focus-visible {
  outline: 2px solid #0D47A1;
  outline-offset: 2px;
}

.notification-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

:deep(.notification-badge .el-badge__content.is-fixed) {
  top: 1px !important;
  right: 2px !important;
  transform: translateY(-25%) translateX(45%) !important;
  height: 18px !important;
  min-width: 18px !important;
  line-height: 14px !important;
  padding: 0 5px !important;
  font-size: 10px !important;
  font-weight: 700 !important;
  border: 2px solid #0f172a !important;
  background-color: #E53935 !important;
  box-shadow: 0 1px 4px rgba(229, 57, 53, 0.5) !important;
}

.bell-icon {
  font-size: 0.95rem;
  line-height: 1;
  transition: transform 0.2s ease, color 0.2s ease;
}

.notification-trigger:hover .bell-icon {
  transform: rotate(12deg) scale(1.08);
  color: #ffffff;
}

/* ================================================================
   VARIANTE SIDEBAR — campana integrada en la fila "Notificaciones"
   ================================================================ */
.notification-alerts-container--sidebar {
  flex-shrink: 0;
}

.notification-alerts-container--sidebar .notification-trigger {
  width: 20px;
  height: 20px;
  border-radius: 0;
  background: transparent;
  border: none;
  color: inherit;
  transition: none;
}

.notification-alerts-container--sidebar .notification-trigger:hover {
  background: transparent;
  border-color: transparent;
  color: #ffffff !important;
}

.notification-alerts-container--sidebar .notification-trigger:hover .menu--icon {
  color: #ffffff !important;
}

.notification-alerts-container--sidebar .notification-trigger:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

/* Sidebar colapsado: el badge se apoya sobre la campana (el texto está oculto) */
.notification-alerts-container--sidebar :deep(.notification-badge .el-badge__content.is-fixed) {
  top: -7px !important;
  right: -9px !important;
  transform: none !important;
  height: 16px !important;
  min-width: 16px !important;
  line-height: 12px !important;
  padding: 0 4px !important;
  font-size: 9.5px !important;
  font-weight: 800 !important;
  border: 2px solid #0f172a !important;
  background-color: #E53935 !important;
  box-shadow: 0 1px 4px rgba(229, 57, 53, 0.55) !important;
}
</style>
